<template>
  <v-card class="mb-3" outlined :class="{ 'grey lighten-4': dim }">
    <v-card-title class="py-2">
      <v-chip small dark :color="color" class="mr-2">{{ chipText }}</v-chip>
      <span class="subtitle-1">{{ whenText }}</span>
    </v-card-title>
    <v-card-text class="body-2">
      <!-- The SAP number is what a caller and the office both quote, so it is a
           line of its own, not a caption in the corner. -->
      <div v-if="order.upcountry" class="caption mb-1">
        รถใหญ่ต่างจังหวัด — {{ order.upcountry.groupLabel }}
        <span v-if="order.upcountry.regNum"> · รถ {{ order.upcountry.regNum }}</span>
      </div>
      <div class="mb-1">
        <strong>เลขที่ SO:</strong>
        <span v-if="order.soNo" class="subtitle-2">{{ order.soNo }}</span>
        <span v-else-if="order.rtNo" class="subtitle-2">RT {{ order.rtNo }}</span>
        <span v-else class="grey--text text--darken-1">นัดหมาย (ยังไม่มีเลขที่ SO)</span>
      </div>
      <div v-if="order.rtNo && order.soNo" class="body-2">เลขที่ RT (รับคืน): {{ order.rtNo }}</div>
      <div v-if="order.returnProducts && order.returnProducts.length" class="caption">
        รับคืน: {{ order.returnProducts.map(p => p.name + ' × ' + p.qty).join(', ') }}
      </div>
      <!-- The order number in the system that booked this, when it was handed
           over from one. Shown beside the SO because it is the other number a
           caller may quote, and the only link back before an SO exists. -->
      <div v-if="order.sourceRef" class="mb-1">
        <strong>เลขที่คำสั่งซื้อ (ขายออนไลน์):</strong> {{ order.sourceRef }}
      </div>
      <div><strong>{{ order.custName || '-' }}</strong> · {{ order.tel || '-' }}</div>
      <div>{{ order.address || '-' }}</div>
      <div v-if="order.landmark" class="grey--text text--darken-1">จุดสังเกต: {{ order.landmark }}</div>
      <div v-if="mapUrl"><a :href="mapUrl" target="_blank" rel="noopener">พิกัด (เปิดแผนที่)</a></div>
      <div v-if="order.products.length" class="mt-2">
        <strong>สินค้า:</strong> {{ productText }}
      </div>
      <div v-if="order.owner" class="mt-1"><strong>ผู้จอง:</strong> {{ $personName(order.owner) }}</div>
      <div v-if="order.regNum" class="mt-1">
        <strong>รถ:</strong> {{ order.regNum }}<template v-if="order.driverName"> · คนขับ {{ order.driverName }}</template>
      </div>
      <div v-if="stepText" class="mt-1"><strong>เวลา:</strong> {{ stepText }}</div>
      <div v-if="order.status === 'failed'" class="mt-1 red--text">
        สาเหตุ: {{ rejectText }}
      </div>
      <div v-if="order.cancelled" class="mt-1">
        ยกเลิกโดย {{ $personName(order.cancelled.by) || '-' }}<template v-if="cancelledAtText"> เมื่อ {{ cancelledAtText }}</template>
      </div>
      <div v-if="order.status === 'sapDispatched' && order.transNo" class="mt-1">
        ใบจัดรถเลขที่ {{ order.transNo }}
      </div>
      <!-- Who booked, moved, cancelled or asked to, and who approved -- so a
           problem can be traced. Collapsed: the card is read for its status. -->
      <div v-if="timeline.length" class="mt-2">
        <a class="caption" @click="showHistory = !showHistory">
          <v-icon x-small color="primary">{{ showHistory ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          ประวัติการเปลี่ยนแปลง ({{ timeline.length }})
        </a>
        <div v-if="showHistory" class="mt-1">
          <div v-for="(e, i) in timeline" :key="i" class="caption">• {{ lineOf(e) }}</div>
        </div>
      </div>
    </v-card-text>
    <v-card-text v-if="action === 'pending' && order.pendingProposal" class="pt-0">
      <v-chip small color="amber darken-2" dark>
        {{ order.pendingProposal.kind === 'cancel'
          ? 'มีคำขอยกเลิก'
          : `มีคำขอเลื่อนไป ${order.pendingProposal.date} (${slotLabel(order.pendingProposal.slot)})` }}
        โดย {{ $personName(order.pendingProposal.by) }} — รอยืนยัน
      </v-chip>
    </v-card-text>
    <v-card-text v-if="action === 'dispatched'" class="pt-0 body-2 orange--text text--darken-3">
      จัดรถแล้ว — โปรดแจ้งฝ่ายจัดส่ง{{ order.regNum ? ` (รถ ${order.regNum}, ${order.date})` : '' }}
    </v-card-text>
    <v-card-actions v-if="action === 'manage' || action === 'propose' || bookable || attachable">
      <v-btn v-if="action === 'manage'" text color="orange darken-2" @click="$emit('manage', order)">
        จัดการใน จองวันส่งสินค้า
      </v-btn>
      <v-btn v-if="bookable" color="orange accent-3" dark @click="$emit('book', order)">
        จองวันส่ง
      </v-btn>
      <template v-if="action === 'propose'">
        <v-btn v-if="!order.upcountry" text color="orange darken-2" @click="$emit('propose', order, 'move')">ขอเลื่อน</v-btn>
        <v-btn text color="error" @click="$emit('propose', order, 'cancel')">ขอยกเลิก</v-btn>
      </template>
      <!-- Beside whatever else the card offers: the page decides who may
           (orderSearch.canAttachSo), as it decides action. -->
      <v-btn v-if="attachable" outlined color="indigo" @click="$emit('attach', order)">ใส่ SO</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
// One order on ค้นหาการจอง. Draws what orderSearch.mergeOrders / sapOrder built
// and decides nothing: whether it may be managed is the page's to pass in.
import moment from 'moment-timezone'
import R from '@/components/bookingRules'
import H from '@/components/bookingHistory'

const COLOR = {
  unbooked: 'grey darken-1',
  sapCancelled: 'grey',
  sapClosed: 'grey',
  sapDispatched: 'blue-grey',
  booked: 'blue',
  planned: 'indigo',
  dispatched: 'deep-purple',
  enroute: 'orange',
  arrived: 'orange darken-2',
  installing: 'amber darken-3',
  done: 'green',
  failed: 'red',
  cancelled: 'grey'
}

const STEP_TEXT = [
  ['departed', 'ออกเดินทาง'],
  ['arrived', 'ถึง'],
  ['installStarted', 'เริ่มติดตั้ง'],
  ['finished', 'เสร็จ'],
  ['rejected', 'แจ้งส่งไม่สำเร็จ']
]

// Buddhist-era, as every other date a shop reads on this app.
function thaiDate (m) {
  return m.format('DD/MM/') + (m.year() + 543)
}

export default {
  name: 'orderCard',
  props: {
    order: { type: Object, required: true },
    action: { type: String, default: 'none' },
    // ใส่ SO on a นัดหมาย still waiting for its SO (orderSearch.canAttachSo).
    attachable: { type: Boolean, default: false }
  },
  data () {
    return { showHistory: false }
  },
  computed: {
    timeline () {
      return this.order.historySource
        ? H.timeline(this.order.historySource, { nameOf: this.$personName, today: R.todayStr() })
        : []
    },
    dim () { return ['cancelled', 'sapCancelled', 'sapClosed'].indexOf(this.order.status) !== -1 },
    color () { return COLOR[this.order.status] || 'grey' },
    bookable () { return this.order.status === 'unbooked' },
    chipText () {
      return this.order.status === 'dispatched' && this.order.regNum
        ? `${this.order.label} · ${this.order.regNum}`
        : this.order.label
    },
    whenText () {
      if (!this.order.date) return ''
      const day = thaiDate(moment.tz(this.order.date, R.TZ))
      const slot = R.SLOT_LABEL_TH[this.order.slot]
      if (this.order.time) return `${day} · ${this.order.time}`
      return slot ? `${day} · ${slot}` : day
    },
    mapUrl () {
      const p = R.parseGeo(this.order.geo)
      return p ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(p.lat + ',' + p.lng) : ''
    },
    productText () {
      return this.order.products.map(p => p.qty ? `${p.name} × ${p.qty}` : p.name).join(', ')
    },
    stepText () {
      const c = this.order.clocks || {}
      return STEP_TEXT.filter(([k]) => c[k]).map(([k, label]) => `${label} ${c[k]}`).join(' · ')
    },
    rejectText () {
      const reasons = (this.order.rejectReasons || []).join(', ') || '-'
      return this.order.rejectNote ? `${reasons} — ${this.order.rejectNote}` : reasons
    },
    cancelledAtText () {
      const at = this.order.cancelled && this.order.cancelled.at
      if (!at) return ''
      const m = moment.tz(at, R.TZ)
      return m.isValid() ? `${thaiDate(m)} ${m.format('HH:mm')}` : ''
    }
  },
  methods: {
    slotLabel (s) { return R.SLOT_LABEL_TH[s] || s },
    lineOf (e) { return H.lineOf(e) }
  }
}
</script>
